<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { renderSafeMarkdown } from '../lib/markdown';
  import {
    getGeorgeSettings, updateGeorgeSettings, getGeorgeState,
    georgeControl, connectGeorgeWS, getActiveAgents,
    getGeorgeBotAccount, updateGeorgeBotAccount, resetGeorgeBotSession,
    getClients, launchStrategyCall,
    getGeorgeSpeechConfig, updateGeorgeSpeechConfig,
    getCallHistory, readCall, readTranscript, deleteCall, saveFileContent,
    getUpcomingEvents, inviteGeorgeToEvent, removeGeorgeFromEvent, toggleAutoInvite,
    downloadCallBriefPdf,
    getVoicePreviewUrl, getPronunciationTestUrl, getVoiceCredits,
    type GeorgeState, type GeorgeMode, type GeorgeSettings, type GeorgeSpeakingState, type TranscriptLine,
    type GeorgeBotAccount, type CallEntry, type ActiveAgent,
  } from '../lib/api';
  import UpgradeModal from '../lib/UpgradeModal.svelte';

  // A 402 means "pay to continue" (voice credits spent, trial over, Tier-3 feature).
  // Show the plan picker — an alert saying "Failed:" offers no way out.
  let showUpgrade = $state(false);
  function handledAsPaywall(e: any): boolean {
    if (e?.status !== 402) return false;
    showUpgrade = true;
    return true;
  }

  let callState = $state<GeorgeState>({
    mode: 'prompted',
    activated: true,
    in_call: false,
    client: '',
    call_start: null,
    speaking_state: 'idle',
    paused: false,
    transcript_length: 0,
    agent_connected: false,
  });

  // Active agent (ONE per user)
  let activeAgent = $state<ActiveAgent | null>(null);
  import { agentName as agentNameStore, agentTitle as agentTitleStore } from '../lib/stores';
  let agentName = $state(localStorage.getItem('george_agent_name') || '');
  let endpointingMs = $state(parseInt(localStorage.getItem('george_endpointing_ms') || '600', 10));
  let selectedVoice = $state(localStorage.getItem('george_voice_id') || 'A41HRDgOrF1mgUtjuSGM');
  let sessionHealthy = $state(true);
  let previewingVoice = $state('');
  let previewAudio: HTMLAudioElement | null = null;
  // null until /voice-credits answers. It used to default to unlimited, so the first paint —
  // and any failed fetch — showed a Base-plan user the Full-plan auto-join control.
  let voiceCredits = $state<{ unlimited: boolean; remaining: number; total: number } | null>(null);

  async function setVoice(voiceId: string) {
    selectedVoice = voiceId;
    localStorage.setItem('george_voice_id', voiceId);
    await updateGeorgeSettings({ voice_id: voiceId } as any);
  }

  function togglePreview(voiceId: string) {
    if (previewAudio) {
      previewAudio.pause();
      previewAudio = null;
    }
    if (previewingVoice === voiceId) {
      previewingVoice = '';
      return;
    }
    previewingVoice = voiceId;
    previewAudio = new Audio(getVoicePreviewUrl(voiceId));
    previewAudio.onended = () => { previewingVoice = ''; previewAudio = null; };
    previewAudio.onerror = () => { previewingVoice = ''; previewAudio = null; };
    previewAudio.play();
  }

  let testingPron = $state('');
  function testPronunciation(replacement: string) {
    if (previewAudio) {
      previewAudio.pause();
      previewAudio = null;
    }
    if (testingPron === replacement) {
      testingPron = '';
      return;
    }
    testingPron = replacement;
    previewAudio = new Audio(getPronunciationTestUrl(replacement));
    previewAudio.onended = () => { testingPron = ''; previewAudio = null; };
    previewAudio.onerror = () => { testingPron = ''; previewAudio = null; };
    previewAudio.play();
  }

  let botAccount = $state<GeorgeBotAccount>({
    bot_email: 'partner@dealton.ai',
    bot_display_name: '',
    custom_account: false,
    session_saved: false,
  });
  let customEmail = $state('');
  let customDisplayName = $state('');

  // Strategy call
  let clients = $state<string[]>([]);
  let selectedClient = $state('');
  let strategyLaunching = $state(false);
  let strategyMeetUrl = $state('');

  // Speech config
  let abbreviations = $state<Record<string, string>>({});
  let pronunciations = $state<Record<string, {replacement: string, whole_word: boolean}>>({});
  let newAbbrKey = $state('');
  let newAbbrValue = $state('');
  let newPronKey = $state('');
  let newPronValue = $state('');
  let newPronWholeWord = $state(true);

  let transcript = $state<TranscriptLine[]>([]);
  let ws: WebSocket | null = null;
  let connected = $state(false);
  let transcriptEl: HTMLDivElement;
  let autoScroll = $state(true);

  // ── Call settings subpage (in-page view toggle) + header date ──
  let settingsView = $state(false);
  function goSettings(on: boolean) {
    settingsView = on;
    window.scrollTo(0, 0);
  }
  const todayLabel = new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // Voice options (shared by Set-up preview + Call-settings picker)
  const VOICES = [
    { id: 'WLKp2jV6nrS8aMkPPDRO', label: 'Australian Male', desc: 'warm, measured' },
    { id: 'A41HRDgOrF1mgUtjuSGM', label: 'American Male', desc: 'clear, direct' },
    { id: 'X03mvPuTfprif8QBAVeJ', label: 'American Female', desc: 'bright, brisk' },
  ];

  // ─── WebSocket connection ───────────────────────────────────────────────────

  function isMyAgent(msg: any): boolean {
    // Accept messages that have no agent_id (legacy) or match our active agent
    if (!msg.agent_id) return true;
    if (!activeAgent) return true; // no agent yet, accept all
    return msg.agent_id === activeAgent.agent_id;
  }

  function connect() {
    ws = connectGeorgeWS({
      onState: (data) => {
        // Filter: only update if this is our agent
        if (data.agent_id && activeAgent && data.agent_id !== activeAgent.agent_id) return;
        callState = data;
        // Update activeAgent reference if it matches
        if (data.agent_id && activeAgent && data.agent_id === activeAgent.agent_id) {
          activeAgent = { ...activeAgent, ...data };
        }
      },
      onTranscript: (line) => {
        transcript = [...transcript, line];
        if (autoScroll && transcriptEl) {
          requestAnimationFrame(() => {
            transcriptEl.scrollTop = transcriptEl.scrollHeight;
          });
        }
      },
      onTranscriptHistory: (lines) => {
        transcript = lines;
        if (transcriptEl) {
          requestAnimationFrame(() => {
            transcriptEl.scrollTop = transcriptEl.scrollHeight;
          });
        }
      },
      onSpeaking: (data) => {
        callState = { ...callState, speaking_state: data.state };
      },
      onSettings: (data) => {
        callState = { ...callState, mode: data.mode, activated: data.activated };
      },
      onClose: () => {
        connected = false;
        // Only reconnect if we were previously connected (not on initial failure)
        if (ws && ws.readyState !== WebSocket.CONNECTING) {
          setTimeout(connect, 10000);  // 10s backoff
        }
      },
    });

    // Override onmessage to handle new multi-agent message types + filtering
    const origOnMessage = ws.onmessage;
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);

      // Handle agents_list (sent on WS connect)
      if (msg.type === 'agents_list') {
        const agents: ActiveAgent[] = msg.data || [];
        // Pick the first agent as "my" agent (one agent per user)
        activeAgent = agents.length > 0 ? agents[0] : null;
        if (activeAgent) {
          callState = {
            mode: activeAgent.mode as any,
            activated: activeAgent.activated,
            in_call: activeAgent.in_call,
            client: activeAgent.client,
            call_start: activeAgent.call_start,
            speaking_state: activeAgent.speaking_state as any,
            paused: activeAgent.paused,
            transcript_length: activeAgent.transcript_length,
            agent_connected: activeAgent.agent_connected,
          };
        }
        return;
      }

      // Handle agent_created — adopt it as our agent
      if (msg.type === 'agent_created') {
        activeAgent = msg.data;
        return;
      }

      // Handle agent_removed — clear our agent if it matches
      if (msg.type === 'agent_removed') {
        if (activeAgent && msg.agent_id === activeAgent.agent_id) {
          activeAgent = null;
          callState = {
            ...callState,
            in_call: false,
            speaking_state: 'idle',
            agent_connected: false,
            client: '',
            call_start: null,
          };
          transcript = [];
        }
        return;
      }

      // Filter all other messages by agent_id
      if (!isMyAgent(msg)) return;

      // Delegate to the original handler for standard message types
      if (origOnMessage) {
        origOnMessage.call(ws!, event);   // inside ws.onmessage — ws is assigned here
      }
    };

    ws.onopen = () => { connected = true; };
  }

  onMount(async () => {
    // Load initial callState via REST
    try {
      const [s, settings, bot, cl, agents] = await Promise.all([
        getGeorgeState(), getGeorgeSettings(), getGeorgeBotAccount(), getClients(true),
        getActiveAgents().catch(() => []),
      ]);
      // Load calendar, call history, and credits in background
      loadCalendar();
      loadCallHistory();
      getVoiceCredits().then(c => voiceCredits = c).catch(() => {});
      callState = { ...s, mode: settings.mode, activated: settings.activated };
      if (settings.endpointing_ms != null) {
        endpointingMs = settings.endpointing_ms;
      }
      // DB is the source of truth for voice — sync local callState + cache
      if (settings.voice_id) {
        selectedVoice = settings.voice_id;
        localStorage.setItem('george_voice_id', settings.voice_id);
      }
      botAccount = bot;
      clients = cl;

      // Adopt the first active agent (one agent per user)
      if (agents.length > 0) {
        activeAgent = agents[0];
        callState = {
          mode: activeAgent.mode as any,
          activated: activeAgent.activated,
          in_call: activeAgent.in_call,
          client: activeAgent.client,
          call_start: activeAgent.call_start,
          speaking_state: activeAgent.speaking_state as any,
          paused: activeAgent.paused,
          transcript_length: activeAgent.transcript_length,
          agent_connected: activeAgent.agent_connected,
        };
      }

      if (bot.custom_account) {
        customEmail = bot.bot_email;
        customDisplayName = bot.bot_display_name;
      }
      // Load speech config
      try {
        const sc = await getGeorgeSpeechConfig();
        abbreviations = sc.abbreviations || {};
        // Normalize pronunciation entries: plain strings → {replacement, whole_word}
        const rawPron = sc.pronunciations || {};
        const normalized: Record<string, {replacement: string, whole_word: boolean}> = {};
        for (const [k, v] of Object.entries(rawPron)) {
          if (typeof v === 'string') {
            normalized[k] = { replacement: v, whole_word: true };
          } else {
            normalized[k] = v as {replacement: string, whole_word: boolean};
          }
        }
        pronunciations = normalized;
      } catch { /* speech config endpoint may not exist yet */ }
    } catch { /* backend may not be ready */ }

    connect();
  });

  onDestroy(() => {
    if (ws) {
      ws.onclose = null;  // prevent reconnect
      ws.close();
    }
  });

  // ─── Handlers ───────────────────────────────────────────────────────────────

  function myAgentId(): string {
    return activeAgent?.agent_id || '';
  }

  async function toggleMode() {
    const newMode = callState.mode === 'prompted' ? 'conversational' : 'prompted';
    await setMode(newMode);
  }

  async function setMode(newMode: GeorgeMode) {
    await updateGeorgeSettings({ mode: newMode, agent_id: myAgentId() || undefined } as any);
    callState = { ...callState, mode: newMode };
  }

  async function handlePause() {
    await georgeControl('pause', myAgentId());
  }

  async function handleResume() {
    await georgeControl('continue', myAgentId());
  }

  async function handleReEvaluate() {
    // Unpause + immediately trigger a fresh response based on what was said during pause
    await georgeControl('continue', myAgentId());
    await georgeControl('start', myAgentId());
  }

  async function handleAnswer() {
    await georgeControl('start', myAgentId());
  }

  async function handleRejoin() {
    await georgeControl('rejoin', myAgentId());
  }

  async function handleLeave() {
    if (!confirm('Leave the call? Transcript will be saved.')) return;
    await georgeControl('leave', myAgentId());
    callState = { ...callState, in_call: false, paused: false };
    // Keep activeAgent for 60s so Rejoin still works
    setTimeout(() => { activeAgent = null; }, 60000);
  }

  async function handleActivate() {
    await georgeControl('activate', myAgentId());
    callState = { ...callState, activated: true };
  }

  async function handleDeactivate() {
    await georgeControl('deactivate', myAgentId());
    callState = { ...callState, activated: false };
  }

  function saveAgentName() {
    localStorage.setItem('george_agent_name', agentName);
  }

  function updateEndpointing(value: number) {
    endpointingMs = value;
    localStorage.setItem('george_endpointing_ms', String(value));
    // Persist to backend per-user
    updateGeorgeSettings({ endpointing_ms: value } as any).catch(() => {});
    // Also send to running agent via WebSocket
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: 'setting',
        key: 'endpointing_ms',
        value: value,
        agent_id: myAgentId(),
      }));
    }
  }

  // ─── Strategy call handler ─────────────────────────────────────────────────

  async function handleLaunchCall(mode: 'strategy' | 'client' = 'strategy') {
    if (!selectedClient || strategyLaunching) return;
    strategyLaunching = true;
    strategyMeetUrl = '';
    try {
      const res = await launchStrategyCall(selectedClient, mode) as any;
      if (res.ok && res.meeting_url) {
        strategyMeetUrl = res.meeting_url;
        window.open(res.meeting_url, '_blank');
        // Adopt the new agent
        if (res.agent_id) {
          try {
            const agents = await getActiveAgents();
            const mine = agents.find((a: ActiveAgent) => a.agent_id === res.agent_id);
            if (mine) activeAgent = mine;
          } catch { /* will be picked up via WS */ }
        }
      } else {
        alert(res.error || 'Failed to launch call');
      }
    } catch (e: any) {
      if (!handledAsPaywall(e)) alert(`Error: ${e.message}`);
    } finally {
      strategyLaunching = false;
    }
  }

  // ─── Bot account handlers ──────────────────────────────────────────────────

  async function toggleCustomAccount() {
    const useCustom = !botAccount.custom_account;
    if (useCustom) {
      // Switch to custom — keep current custom fields
      const res = await updateGeorgeBotAccount({
        custom_account: true,
        bot_email: customEmail || botAccount.bot_email,
        bot_display_name: customDisplayName || botAccount.bot_display_name,
      });
      botAccount = { bot_email: res.bot_email, bot_display_name: res.bot_display_name, custom_account: res.custom_account, session_saved: botAccount.session_saved };
      customEmail = res.bot_email;
      customDisplayName = res.bot_display_name;
    } else {
      // Revert to default
      const res = await updateGeorgeBotAccount({ custom_account: false });
      botAccount = { bot_email: res.bot_email, bot_display_name: res.bot_display_name, custom_account: res.custom_account, session_saved: false };
    }
  }

  async function saveCustomAccount() {
    if (!customEmail) return;
    const res = await updateGeorgeBotAccount({
      bot_email: customEmail,
      bot_display_name: customDisplayName || $agentNameStore || 'AI Partner',
    });
    botAccount = { bot_email: res.bot_email, bot_display_name: res.bot_display_name, custom_account: res.custom_account, session_saved: false };
  }

  async function handleResetSession() {
    await resetGeorgeBotSession();
    botAccount = { ...botAccount, session_saved: false };
  }

  // ─── Speech config handlers ────────────────────────────────────────────────

  async function addAbbreviation() {
    if (!newAbbrKey.trim() || !newAbbrValue.trim()) return;
    abbreviations = { ...abbreviations, [newAbbrKey.trim()]: newAbbrValue.trim() };
    newAbbrKey = '';
    newAbbrValue = '';
    await updateGeorgeSpeechConfig({ abbreviations });
  }

  async function removeAbbreviation(key: string) {
    const { [key]: _, ...rest } = abbreviations;
    abbreviations = rest;
    await updateGeorgeSpeechConfig({ abbreviations });
  }

  async function addPronunciation() {
    if (!newPronKey.trim() || !newPronValue.trim()) return;
    pronunciations = { ...pronunciations, [newPronKey.trim()]: { replacement: newPronValue.trim(), whole_word: newPronWholeWord } };
    newPronKey = '';
    newPronValue = '';
    newPronWholeWord = true;
    await updateGeorgeSpeechConfig({ pronunciations });
  }

  async function togglePronunciationWholeWord(key: string) {
    const entry = pronunciations[key];
    if (!entry) return;
    pronunciations = { ...pronunciations, [key]: { ...entry, whole_word: !entry.whole_word } };
    await updateGeorgeSpeechConfig({ pronunciations });
  }

  async function removePronunciation(key: string) {
    const { [key]: _, ...rest } = pronunciations;
    pronunciations = rest;
    await updateGeorgeSpeechConfig({ pronunciations });
  }

  // ─── Call History ──────────────────────────────────────────────────────────

  let callHistory = $state<CallEntry[]>([]);
  let callHistoryLoaded = $state(false);
  let callHistoryFilter = $state('');
  let expandedCall = $state<string | null>(null);       // "client/type/filename"
  let callContent = $state('');
  let editingCallSummary = $state(false);
  let editCallText = $state('');
  let transcriptContent = $state('');
  let showTranscript = $state(false);
  let fullscreenCall = $state(false);

  // Calendar
  // Mirrors the getUpcomingEvents() return shape in api.ts (optional fields incl.
  // call_type / has_external / invite_type). Kept in sync so the assignment type-checks.
  interface CalendarEvent {
    id: string;
    summary: string;
    start: string;
    attendees: string[];
    george_invited: boolean;
    has_meeting_url: boolean;
    has_client?: boolean;
    has_external?: boolean;
    call_type?: 'client' | 'external' | 'internal';
    matched_client?: string;
    invite_type?: 'manual' | 'auto' | null;
  }
  let calendarEvents = $state<CalendarEvent[]>([]);
  let autoInvite = $state(false);
  let calendarLoaded = $state(false);
  let calendarLoading = $state(false);

  async function loadCalendar() {
    calendarLoading = true;
    try {
      const data = await getUpcomingEvents(14);
      calendarEvents = data.events || [];
      autoInvite = data.auto_invite || false;
      calendarLoaded = true;
    } catch { }
    calendarLoading = false;
  }

  async function handleInviteToggle(ev: CalendarEvent) {
    try {
      if (ev.george_invited) {
        await removeGeorgeFromEvent(ev.id);
        ev.george_invited = false;
      } else {
        await inviteGeorgeToEvent(ev.id);
        ev.george_invited = true;
      }
      calendarEvents = [...calendarEvents];
      // Refresh credits after invite/uninvite
      getVoiceCredits().then(c => voiceCredits = c).catch(() => {});
    } catch (e: any) {
      console.error('Invite toggle failed:', e);
      if (!handledAsPaywall(e)) alert(`Failed: ${e?.message || e}`);
    }
  }

  let autoInviteLoading = false;
  async function handleAutoInviteToggle() {
    if (autoInviteLoading) return;
    autoInviteLoading = true;
    try {
      const res = await toggleAutoInvite();
      autoInvite = res.auto_invite;
      // Refresh calendar events from server — DB is source of truth for george_invited
      const data = await getUpcomingEvents(14);
      calendarEvents = data.events || [];
      getVoiceCredits().then(c => voiceCredits = c).catch(() => {});
    } catch (e: any) {
      console.error('Auto-invite toggle failed:', e);
      // Was silent — the switch simply did not move and nothing said why.
      if (!handledAsPaywall(e)) alert(`Couldn't change that: ${e?.message || e}`);
    } finally {
      autoInviteLoading = false;
    }
  }
  // Pre-call briefs — click downloads the brief as a PDF (generated on the
  // server if it doesn't exist yet, so the first click may take a moment).
  let downloadingBrief = $state<string | null>(null);

  async function downloadBrief(ev: CalendarEvent) {
    if (downloadingBrief) return;
    downloadingBrief = ev.id;
    try {
      await downloadCallBriefPdf(ev.id);
    } catch (e: any) {
      alert(`Couldn't get the brief: ${e?.message || e}`);
    }
    downloadingBrief = null;
  }

  let deletingCall = $state<string | null>(null);

  async function loadCallHistory() {
    try {
      callHistory = await getCallHistory(callHistoryFilter);
      callHistoryLoaded = true;
    } catch { /* */ }
  }

  async function toggleCallExpand(entry: CallEntry) {
    const key = `${entry.client}/${entry.type === 'meeting' ? 'meetings' : 'strategy'}/${entry.filename}`;
    if (expandedCall === key) {
      expandedCall = null;
      callContent = '';
      transcriptContent = '';
      showTranscript = false;
      return;
    }
    expandedCall = key;
    showTranscript = false;
    transcriptContent = '';
    try {
      const folder = entry.type === 'meeting' ? 'meetings' : 'strategy';
      const res = await readCall(entry.client, folder, entry.filename);
      callContent = res.content;
    } catch { callContent = 'Failed to load'; }
  }

  async function loadTranscript(entry: CallEntry) {
    if (!entry.has_transcript || !entry.transcript_filename) return;
    showTranscript = !showTranscript;
    if (!showTranscript) { transcriptContent = ''; return; }
    try {
      const folder = entry.type === 'meeting' ? 'meetings' : 'strategy';
      const res = await readTranscript(entry.client, folder, entry.transcript_filename);
      transcriptContent = res.content;
    } catch { transcriptContent = 'Failed to load transcript'; }
  }

  async function handleDeleteCall(entry: CallEntry) {
    const key = `${entry.client}/${entry.type}/${entry.filename}`;
    if (deletingCall === key) {
      // Second click = confirm
      try {
        const folder = entry.type === 'meeting' ? 'meetings' : 'strategy';
        await deleteCall(entry.client, folder, entry.filename);
        callHistory = callHistory.filter(c =>
          !(c.client === entry.client && c.type === entry.type && c.filename === entry.filename)
        );
        if (expandedCall?.startsWith(`${entry.client}/${folder}/${entry.filename}`)) {
          expandedCall = null;
          callContent = '';
          transcriptContent = '';
        }
      } catch { /* */ }
      deletingCall = null;
    } else {
      deletingCall = key;
      // Reset confirmation after 3s
      setTimeout(() => { if (deletingCall === key) deletingCall = null; }, 3000);
    }
  }

  // ─── Derived display values ─────────────────────────────────────────────────

  const stateLabels: Record<GeorgeSpeakingState, string> = {
    idle: 'Idle',
    listening: 'Listening',
    thinking: 'Thinking...',
    speaking: 'Speaking',
    paused: 'Paused',
  };

  function formatCallDuration(start: string | null): string {
    if (!start) return '';
    // Ensure UTC parsing — append Z if no timezone indicator
    const utcStart = start.endsWith('Z') || start.includes('+') || start.includes('-', 10) ? start : start + 'Z';
    const diff = Date.now() - new Date(utcStart).getTime();
    if (diff < 0) return '0:00';
    const mins = Math.floor(diff / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  function handleTranscriptScroll() {
    if (!transcriptEl) return;
    const { scrollTop, scrollHeight, clientHeight } = transcriptEl;
    autoScroll = scrollHeight - scrollTop - clientHeight < 40;
  }
</script>

<div class="calls">
{#if !settingsView}

  <!-- ══════════ MAIN — Live calls ══════════ -->
  <div class="page-head">
    <div>
      <h1 class="page-title">Live calls</h1>
      <div class="page-sub">
        <span class="date">{todayLabel}</span>
        <span class="sep">·</span>
        <span class="george"><span class="spark">✦</span> {$agentNameStore || 'George'}{$agentTitleStore ? ` — ${$agentTitleStore}` : ''}</span>
        <span class="sep">·</span>
        <span class="conn" class:live={connected}>{connected ? 'Dashboard live' : 'Reconnecting…'}</span>
      </div>
    </div>
    <button class="navlink" onclick={() => goSettings(true)}>⚙ Call settings →</button>
  </div>

  <!-- ── ON A CALL — pinned, quiet when idle ── -->
  <div class="panel oc-panel" class:on={!!activeAgent} class:islive={callState.in_call && !!activeAgent}>
    <div class="pb">
      <div class="oc-strip">
        <span class="sd"></span>
        {#if callState.in_call && activeAgent}
          <span class="livetag">Live</span>
          <span class="oc-txt">
            {#if callState.call_start}{formatCallDuration(callState.call_start)} ·{' '}{/if}<strong>{callState.client.replace(/_/g, ' ')}</strong> · {stateLabels[callState.speaking_state]}
          </span>
          <span class="oc-agent" class:online={activeAgent.agent_connected}>{activeAgent.agent_connected ? 'Agent online' : 'Agent offline'}</span>
          {#if activeAgent.meeting_url}<a class="oc-join" href={activeAgent.meeting_url} target="_blank" rel="noreferrer">Join →</a>{/if}
        {:else if activeAgent}
          <span class="oc-txt">Call ended — you can rejoin for a moment, or leave to save the transcript.</span>
        {:else}
          <span class="oc-txt muted">No active call — join from an upcoming call below, and your controls appear here.</span>
        {/if}
      </div>

      {#if activeAgent}
        <div class="toolbar">
          <button class="tbtn primary" onclick={handleAnswer} disabled={!callState.in_call || callState.speaking_state === 'speaking' || callState.paused} title="Trigger a response to what was just said">
            <span class="ic">✦</span>Answer
          </button>
          <button class="tbtn" onclick={() => callState.paused ? handleResume() : handlePause()} disabled={!callState.in_call} title="Mute — stops listening and speaking until you resume">
            <span class="ic">{callState.paused ? '▶' : '⏸'}</span>{callState.paused ? 'Resume' : 'Pause'}
          </button>
          <button class="tbtn" onclick={handleReEvaluate} disabled={!callState.in_call || !callState.paused} title="Process what was said during the pause and respond fresh">
            <span class="ic">↻</span>Re-evaluate
          </button>
          <button class="tbtn" onclick={handleRejoin} disabled={!activeAgent} title="Reconnect to the call if disconnected">
            <span class="ic">↩</span>Rejoin
          </button>
          <span class="tbtn-spacer"></span>
          <button class="tbtn danger" onclick={handleLeave} disabled={!activeAgent} title="End the session — transcript and summary will be saved">
            <span class="ic">✕</span>Leave call
          </button>
        </div>

        <div class="tbox">
          <div class="th">Live transcript
            {#if callState.in_call}<span class="pill info">● transcribing</span>{/if}
          </div>
          <div class="tlist" bind:this={transcriptEl} onscroll={handleTranscriptScroll}>
            {#if transcript.length === 0}
              <div class="tempty">{callState.in_call ? 'Waiting for speech…' : 'No transcript yet.'}</div>
            {:else}
              {#each transcript as line}
                <div class="turn" class:george={line.is_george}>
                  <span class="spk">
                    {#if line.is_george}<span class="gs">✦</span> {$agentNameStore || 'George'}:{:else if line.speaker}{line.speaker}:{/if}
                  </span>
                  {line.text}
                </div>
              {/each}
            {/if}
          </div>
          {#if !autoScroll && transcript.length > 0}
            <button class="scroll-btn" onclick={() => { autoScroll = true; transcriptEl.scrollTop = transcriptEl.scrollHeight; }}>Scroll to bottom</button>
          {/if}
        </div>
      {/if}
    </div>
  </div>

  <!-- ── YOUR CALLS — upcoming + history ── -->
  <div class="panel">
    <div class="ph"><span class="t">Your calls</span><span class="note">what's next & what happened before</span></div>
    <div class="pb">

      <!-- Upcoming -->
      <div class="cell" style="margin-bottom:11px;">
        <div class="cell-h">Upcoming calls
          {#if voiceCredits && !voiceCredits.unlimited}
            <span class="usage" title="Voice-call credits left this month — shared across your whole organisation">⚡ Voice {voiceCredits.remaining}/{voiceCredits.total}</span>
            {#if voiceCredits.remaining === 0}<span class="usage-reset">resets on the 1st</span>{/if}
          {/if}
          {#if voiceCredits?.unlimited}
            <label class="ww" style="margin-left:auto;"><input type="checkbox" checked={autoInvite} onchange={handleAutoInviteToggle} /> Auto-join client calls</label>
          {:else}
            <label class="ww disabled" style="margin-left:auto;" title={voiceCredits ? 'Upgrade to the Voice plan for auto-join' : 'Checking your plan…'}><input type="checkbox" disabled /> Auto-join client calls</label>
          {/if}
          <button class="refresh" onclick={loadCalendar}><span class="ri">↻</span> {calendarLoading ? 'Loading…' : calendarLoaded ? 'Refresh' : 'Load'}</button>
        </div>

        {#if calendarLoaded}
          {#if calendarEvents.length === 0}
            <div class="cell-empty">No upcoming events found.</div>
          {:else}
            <div class="ulist">
              {#each calendarEvents as ev}
                {@const acc = ev.george_invited ? 'acc-inv' : ev.call_type === 'client' ? 'acc-client' : ev.call_type === 'external' ? 'acc-ext' : 'acc-int'}
                {@const canBrief = !!ev.matched_client}
                <div class="ucall {acc}">
                  <div class="uc-main">
                    <div class="uc-t">
                      {ev.summary}
                      {#if ev.matched_client}<span class="utag client">{ev.matched_client.replace(/_/g, ' ')}</span>
                      {:else if ev.call_type === 'external'}<span class="utag ext">External</span>
                      {:else if ev.call_type === 'internal'}<span class="utag int">Internal</span>{/if}
                    </div>
                    <div class="uc-when">
                      {new Date(ev.start).toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
                      {new Date(ev.start).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <div class="uc-act">
                    {#if canBrief}
                      <button class="today-mini-btn" onclick={() => downloadBrief(ev)} disabled={downloadingBrief === ev.id} title="Download pre-call brief (PDF)">
                        {#if downloadingBrief === ev.id}…{:else}<span class="gs">✦</span> Brief{/if}
                      </button>
                    {/if}
                    {#if ev.call_type !== 'internal' || ev.george_invited}
                      <button class="today-mini-btn" class:invited={ev.george_invited} onclick={() => handleInviteToggle(ev)}>
                        {ev.george_invited ? 'Uninvite' : 'Invite'}
                      </button>
                    {/if}
                  </div>
                </div>
              {/each}
            </div>
          {/if}
        {:else}
          <div class="cell-empty">Click Load to view upcoming calendar events.</div>
        {/if}
      </div>

      <!-- History -->
      <div class="cell alt">
        <div class="cell-h">Call history
          {#if callHistoryLoaded}
            <select class="sel hfilter" style="margin-left:auto;" bind:value={callHistoryFilter} onchange={loadCallHistory}>
              <option value="">All clients</option>
              {#each clients as c}<option value={c}>{c.replace(/_/g, ' ')}</option>{/each}
            </select>
          {/if}
          <button class="refresh" onclick={loadCallHistory}><span class="ri">↻</span> Refresh</button>
        </div>

        {#if callHistoryLoaded}
          {#if callHistory.length === 0}
            <div class="cell-empty">No calls recorded yet.</div>
          {:else}
            <div class="hscroll">
              {#each callHistory as entry}
                {@const key = `${entry.client}/${entry.type === 'meeting' ? 'meetings' : 'strategy'}/${entry.filename}`}
                <div class="hrec" class:open={expandedCall === key}>
                  <div class="hrec-h" role="button" tabindex="0"
                    onclick={() => toggleCallExpand(entry)}
                    onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCallExpand(entry); } }}>
                    <span class="rtag" class:call={entry.type === 'strategy'} class:meeting={entry.type !== 'strategy'}>{entry.type === 'strategy' ? 'STRATEGY' : 'MEETING'}</span>
                    <span class="hrec-cli">{entry.client.replace(/_/g, ' ')}</span>
                    <span class="hrec-date">{entry.date}</span>
                    <span class="hrec-act">
                      {#if entry.has_transcript}
                        <button class="today-mini-btn" onclick={(e) => { e.stopPropagation(); if (expandedCall === key) loadTranscript(entry); else { toggleCallExpand(entry).then(() => loadTranscript(entry)); } }}>Transcript</button>
                      {/if}
                      <button class="today-mini-btn" class:danger={deletingCall === `${entry.client}/${entry.type}/${entry.filename}`}
                        onclick={(e) => { e.stopPropagation(); handleDeleteCall(entry); }}>
                        {deletingCall === `${entry.client}/${entry.type}/${entry.filename}` ? 'Confirm?' : 'Delete'}
                      </button>
                    </span>
                  </div>

                  {#if expandedCall === key}
                    <div class="hrec-body" class:fullscreen={fullscreenCall}>
                      <div class="hrec-tabs">
                        {#if showTranscript && transcriptContent}
                          <button class="htab" class:active={!showTranscript} onclick={() => showTranscript = false}>Summary</button>
                          <button class="htab" class:active={showTranscript} onclick={() => showTranscript = true}>Transcript</button>
                        {:else if entry.has_transcript}
                          <button class="htab active">Summary</button>
                          <button class="htab" onclick={() => loadTranscript(entry)}>Transcript</button>
                        {/if}
                        <button class="today-mini-btn" style="margin-left:auto;" onclick={() => fullscreenCall = !fullscreenCall}>{fullscreenCall ? 'Exit Fullscreen' : 'Fullscreen'}</button>
                        {#if !showTranscript}
                          <button class="today-mini-btn" onclick={() => { editingCallSummary = !editingCallSummary; if (editingCallSummary) editCallText = callContent; }}>{editingCallSummary ? 'Cancel' : 'Edit'}</button>
                        {/if}
                      </div>
                      {#if showTranscript && transcriptContent}
                        <div class="hrec-md">{@html renderSafeMarkdown(transcriptContent)}</div>
                      {:else if editingCallSummary}
                        <textarea class="hrec-edit" bind:value={editCallText} rows="15"></textarea>
                        <button class="today-mini-btn" style="margin-top:6px;" onclick={async () => {
                          const folder = entry.type === 'meeting' ? 'meetings' : 'strategy';
                          await saveFileContent(entry.client, folder, entry.filename, editCallText);
                          callContent = editCallText;
                          editingCallSummary = false;
                        }}>Save</button>
                      {:else}
                        <div class="hrec-md">{@html renderSafeMarkdown(callContent)}</div>
                      {/if}
                    </div>
                  {/if}
                </div>
              {/each}
            </div>
          {/if}
        {:else}
          <div class="cell-empty">No call history yet.</div>
        {/if}
      </div>

    </div>
  </div>

  <!-- ── SET UP — ready check + this-call mode ── -->
  <div class="panel">
    <div class="ph"><span class="t">Set up</span><span class="note">get {$agentNameStore || 'George'} ready, then dial in</span></div>
    <div class="pb">
      <div class="grid2">

        <div class="cell">
          <div class="cell-h">Ready check</div>
          <div class="cell-d">A quick sound & device check before you dial. Change his voice in <button class="inline-link" onclick={() => goSettings(true)}>Call settings</button>.</div>
          <div class="row">
            <button class="today-mini-btn" onclick={() => togglePreview(selectedVoice)}>
              <span class="gs">{previewingVoice === selectedVoice ? '■' : '▶'}</span> Preview voice
              {#if VOICES.find(v => v.id === selectedVoice)}<span class="oldname">· {VOICES.find(v => v.id === selectedVoice)?.label}</span>{/if}
            </button>
            <button class="today-mini-btn" disabled title="Device test — coming soon">Test mic</button>
            <button class="today-mini-btn" disabled title="Device test — coming soon">Test speaker</button>
          </div>
        </div>

        <div class="cell">
          <div class="cell-h">Call Mode <span class="pill info">{callState.mode === 'listener' ? 'Listener' : callState.mode === 'conversational' ? 'Conversational' : 'Prompted'}</span></div>
          <div class="cell-d">How {$agentNameStore || 'George'} shows up. Sets your default for new calls.</div>
          <div class="modeseg">
            <button class:on={callState.mode === 'listener'} onclick={() => callState.mode !== 'listener' && setMode('listener')}>Listener</button>
            <button class:on={callState.mode === 'prompted'} onclick={() => callState.mode !== 'prompted' && setMode('prompted')}>Prompted</button>
            <button class:on={callState.mode === 'conversational'} onclick={() => callState.mode !== 'conversational' && setMode('conversational')}>Conversational</button>
          </div>
          <div class="meaning">
            {#if callState.mode === 'listener'}<b>Listener</b> · joins and transcribes, never speaks.
            {:else if callState.mode === 'conversational'}<b>Conversational</b> · participates when relevant.
            {:else}<b>Prompted</b> · speaks only when you ask him to.{/if}
          </div>
        </div>

      </div>
    </div>
  </div>

  <!-- ── PRACTICE WITH GEORGE — no real client dialed ── -->
  <div class="panel">
    <div class="ph"><span class="t">Practice with {$agentNameStore || 'George'}</span><span class="note">talk it through or test-drive him — no real client is dialed</span></div>
    <div class="pb">
      <div class="cell">
        <div class="cell-d">Pick a client, then run a <b>Strategy Call</b> — plan the deal with {$agentNameStore || 'George'} as your coach — or a <b>Test Client Call</b> to see how he behaves on a real client call. Neither one dials the client.</div>
        <div class="row">
          <select class="sel" bind:value={selectedClient}>
            <option value="">Select client…</option>
            {#each clients as client}<option value={client}>{client.replace(/_/g, ' ')}</option>{/each}
          </select>
          <button class="today-mini-btn" onclick={() => handleLaunchCall('strategy')} disabled={!selectedClient || strategyLaunching || callState.in_call}>{strategyLaunching ? 'Launching…' : 'Strategy Call'}</button>
          <button class="today-mini-btn" onclick={() => handleLaunchCall('client')} disabled={!selectedClient || strategyLaunching || callState.in_call} title="Test how the agent behaves on a real client call">Test Client Call</button>
        </div>
        {#if strategyMeetUrl}
          <div class="meet-note">Meeting started — <a href={strategyMeetUrl} target="_blank" rel="noreferrer">Join call →</a></div>
        {/if}
      </div>
    </div>
  </div>

{:else}

  <!-- ══════════ CALL SETTINGS SUBPAGE ══════════ -->
  <button class="back-crumb" title="Back" onclick={() => goSettings(false)}>
    {'← '}Back to Live Calls
  </button>
  <div class="set-title">Call settings</div>
  <div class="set-sub">How {$agentNameStore || 'George'} runs on your calls. Set once — every new call inherits these.</div>

  <div class="sblock">
    <div class="sh">Voice</div>
    <div class="sbb">
      <div class="d">{$agentNameStore || 'George'}'s voice on every call. Preview before you commit.</div>
      {#each VOICES as voice}
        <div class="vrow" class:sel={selectedVoice === voice.id}>
          <button class="vm" onclick={() => setVoice(voice.id)}><b>{voice.label}</b><span>{voice.desc}</span></button>
          <button class="vp" onclick={() => togglePreview(voice.id)} title={previewingVoice === voice.id ? 'Stop preview' : 'Preview voice'}>{previewingVoice === voice.id ? '■' : '▶'}</button>
        </div>
      {/each}
    </div>
  </div>

  <div class="sblock">
    <div class="sh">Default Call Mode</div>
    <div class="sbb">
      <div class="d">The style every new call starts with.</div>
      <div class="modeseg">
        <button class:on={callState.mode === 'listener'} onclick={() => callState.mode !== 'listener' && setMode('listener')}>Listener</button>
        <button class:on={callState.mode === 'prompted'} onclick={() => callState.mode !== 'prompted' && setMode('prompted')}>Prompted</button>
        <button class:on={callState.mode === 'conversational'} onclick={() => callState.mode !== 'conversational' && setMode('conversational')}>Conversational</button>
      </div>
      <div class="meaning">
        {#if callState.mode === 'listener'}<b>Listener</b> · joins and transcribes, never speaks.
        {:else if callState.mode === 'conversational'}<b>Conversational</b> · participates when relevant.
        {:else}<b>Prompted</b> · speaks only when you ask him to.{/if}
      </div>
    </div>
  </div>

  <div class="sblock">
    <div class="sh">Response Delay</div>
    <div class="sbb">
      <div class="d">How long {$agentNameStore || 'George'} waits after you stop speaking before he replies (600ms–1200ms).</div>
      <div class="sld">
        <input type="range" min="600" max="1200" step="100" value={endpointingMs}
          oninput={(e) => updateEndpointing(parseInt((e.target as HTMLInputElement).value, 10))} />
        <span class="v">{endpointingMs}ms</span>
      </div>
    </div>
  </div>

  <div class="sblock">
    <div class="sh">Speech shortcuts <span class="oldname">(Abbreviation Expansions)</span></div>
    <div class="sbb">
      <div class="d">Spell out shorthand so {$agentNameStore || 'George'} says the full phrase on calls.</div>
      {#each Object.entries(abbreviations) as [key, value]}
        <div class="dict"><span class="k">{key}</span><span class="arr">→</span><span class="v">{value}</span><button class="x" onclick={() => removeAbbreviation(key)} title="Remove">×</button></div>
      {/each}
      {#if Object.keys(abbreviations).length === 0}<div class="cell-empty">No abbreviations configured.</div>{/if}
      <div class="dictadd">
        <input class="inp" type="text" placeholder="Abbreviation" bind:value={newAbbrKey} onkeydown={(e) => e.key === 'Enter' && addAbbreviation()} />
        <input class="inp" type="text" placeholder="Expansion" bind:value={newAbbrValue} onkeydown={(e) => e.key === 'Enter' && addAbbreviation()} />
        <button class="today-mini-btn" onclick={addAbbreviation}>Add</button>
      </div>
    </div>
  </div>

  <div class="sblock">
    <div class="sh">Pronunciation Overrides</div>
    <div class="sbb">
      <div class="d">Correct how {$agentNameStore || 'George'} pronounces specific names & words.</div>
      {#each Object.entries(pronunciations) as [key, entry]}
        <div class="dict">
          <span class="k">{key}</span><span class="arr">→</span><span class="v">{entry.replacement}</span>
          <label class="ww" title="Match whole word only"><input type="checkbox" checked={entry.whole_word} onchange={() => togglePronunciationWholeWord(key)} /> whole word</label>
          <button class="x pron-test" onclick={() => testPronunciation(entry.replacement)} title={testingPron === entry.replacement ? 'Stop' : 'Test pronunciation'}>{testingPron === entry.replacement ? '■' : '▶'}</button>
          <button class="x" onclick={() => removePronunciation(key)} title="Remove">×</button>
        </div>
      {/each}
      {#if Object.keys(pronunciations).length === 0}<div class="cell-empty">No pronunciation overrides configured.</div>{/if}
      <div class="dictadd">
        <input class="inp" type="text" placeholder="Word" bind:value={newPronKey} onkeydown={(e) => e.key === 'Enter' && addPronunciation()} />
        <input class="inp" type="text" placeholder="Pronunciation" bind:value={newPronValue} onkeydown={(e) => e.key === 'Enter' && addPronunciation()} />
        <label class="ww" title="Match whole word only"><input type="checkbox" bind:checked={newPronWholeWord} /> whole word</label>
        <button class="today-mini-btn" onclick={addPronunciation}>Add</button>
      </div>
    </div>
  </div>

{/if}
</div>

<UpgradeModal show={showUpgrade} onClose={() => showUpgrade = false} />

<style>
  .calls {
    /* Page/section titles use the app sans now (font-family: inherit on each),
       matching every other page — the Fraunces serif is no longer used here.
       Only Today's "Hi …" greeting stays serif. See DECISIONS 2026-07-20. */
    --mono: var(--font-mono);
    /* NO page-level max-width / centering. Every other page (Dashboard, Settings,
       Prospecting, Today) fills .main-content full-width, flush-left. This page
       was the ONE exception — a 900px centered column with big side gutters — so
       its outer frame didn't line up with the rest of the app. Match them.
       */
  }

  /* ── page header ── */
  .page-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
  /* Match the app-standard page title (app.css `.page-title`: 24px/700) so the
     calls-page header lines up with every other page instead of being larger. */
  .page-title { font-family: inherit; font-size: 24px; font-weight: 700; margin: 0; }
  .page-sub { display: flex; align-items: center; gap: 9px; flex-wrap: wrap; margin-top: 6px; font-size: 12.5px; color: var(--text-muted); }
  .page-sub .sep { color: var(--text-dim); }
  .page-sub .george { display: inline-flex; align-items: center; gap: 5px; }
  .page-sub .spark { color: var(--accent-ink); font-size: 13px; }
  .conn { display: inline-flex; align-items: center; gap: 5px; color: var(--text-dim); }
  .conn.live { color: var(--text-muted); }

  /* ── panels ── */
  .panel { border: 1px solid var(--border); border-radius: 10px; background: var(--bg-surface); margin-bottom: 10px; overflow: hidden; }
  .ph { display: flex; align-items: center; gap: 9px; padding: 9px 13px; background: var(--bg-hover); border-bottom: 1px solid var(--border); }
  .ph .t { font-family: inherit; font-size: 15px; font-weight: 600; color: var(--text); }
  .ph .note { font-size: 11px; color: var(--text-dim); margin-left: auto; font-style: italic; }
  .pb { padding: 13px 14px; }

  /* ── on a call ── */
  .oc-panel.islive { border-color: color-mix(in srgb, var(--blue) 55%, var(--border)); box-shadow: 0 0 0 1px color-mix(in srgb, var(--blue) 30%, transparent); }
  .oc-strip { display: flex; align-items: center; gap: 9px; font-size: 12px; color: var(--text-muted); }
  .oc-strip .sd { width: 7px; height: 7px; border-radius: 50%; background: var(--text-dim); flex-shrink: 0; }
  .oc-panel.islive .oc-strip .sd { background: var(--blue); }
  .oc-txt { min-width: 0; }
  .oc-txt.muted { color: var(--text-dim); }
  .oc-txt strong { color: var(--text); font-weight: 600; }
  .livetag { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; color: var(--blue); border: 1px solid color-mix(in srgb, var(--blue) 45%, var(--border)); border-radius: 4px; padding: 1px 7px; }
  .oc-agent { margin-left: auto; font-family: var(--font-mono); font-size: 10px; color: var(--text-dim); }
  .oc-agent.online { color: var(--green); }
  .oc-join { font-size: 11.5px; color: var(--accent-ink); }

  .toolbar { display: flex; align-items: stretch; min-height: 42px; border: 1px solid var(--border); border-radius: 10px; background: var(--bg-hover); overflow: hidden; margin-top: 12px; }
  .tbtn { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 7px; justify-content: center; border: none; background: transparent; color: var(--text-muted); font-family: inherit; font-size: 12px; font-weight: 500; padding: 0 15px; cursor: pointer; white-space: nowrap; position: relative; transition: background 0.15s, color 0.15s; }
  .tbtn + .tbtn::before { content: ""; position: absolute; left: 0; top: 50%; transform: translateY(-50%); height: 16px; width: 1px; background: var(--border); }
  .tbtn:hover:not(:disabled) { background: var(--bg-active); color: var(--text); }
  .tbtn:disabled { opacity: 0.4; cursor: default; }
  .tbtn.primary { background: color-mix(in srgb, var(--accent) 14%, transparent); color: var(--accent-ink); font-weight: 600; }
  .tbtn.primary:hover:not(:disabled) { background: color-mix(in srgb, var(--accent) 22%, transparent); }
  .tbtn.primary + .tbtn::before { display: none; }
  .tbtn-spacer { flex: 1 1 auto; }
  .tbtn.danger:hover:not(:disabled) { background: var(--bg-active); color: var(--text); } /* destructive stays neutral — red reserved for deal status (DESIGN-SYSTEM §1) */
  .tbtn .ic { font-size: 12px; opacity: 0.7; font-style: normal; }

  .tbox { border: 1px solid var(--border); border-radius: 8px; background: var(--bg-hover); margin-top: 10px; overflow: hidden; }
  .tbox .th { display: flex; align-items: center; gap: 7px; padding: 8px 12px; border-bottom: 1px solid var(--border); font-size: 11.5px; font-weight: 600; }
  .pill { font-family: var(--font-mono); font-size: 9.5px; border-radius: 20px; padding: 1px 8px; border: 1px solid; pointer-events: none; background: transparent; }
  .pill.info { color: var(--blue); border-color: color-mix(in srgb, var(--blue) 45%, var(--border)); margin-left: auto; }
  .tlist { max-height: 260px; overflow-y: auto; padding: 11px 12px; display: flex; flex-direction: column; gap: 9px; }
  .tempty { font-size: 12px; color: var(--text-dim); }
  .turn { font-size: 12px; line-height: 1.5; }
  .turn .spk { font-weight: 600; color: var(--text-muted); margin-right: 4px; }
  .turn.george { color: var(--text-muted); }
  .turn.george .spk { color: var(--accent-ink); }
  .turn .gs { color: var(--accent-ink); }
  .scroll-btn { margin: 0 12px 11px; background: var(--bg-surface); border: 1px solid var(--border); color: var(--text-muted); border-radius: 6px; padding: 4px 10px; font-size: 11px; cursor: pointer; }

  /* ── cells ── */
  .cell { border: 1px solid var(--border); border-radius: 9px; background: var(--bg-hover); padding: 12px 13px; }
  .cell.alt { background: color-mix(in srgb, var(--bg-hover) 55%, var(--bg-surface)); }
  .cell-h { font-size: 12px; font-weight: 600; display: flex; align-items: center; gap: 7px; margin-bottom: 9px; flex-wrap: wrap; }
  .cell-d { font-size: 11px; color: var(--text-dim); line-height: 1.45; margin-bottom: 9px; }
  .cell-empty { font-size: 12px; color: var(--text-dim); padding: 8px 0; }
  .usage { font-family: var(--font-mono); font-size: 10px; color: var(--orange); border: 1px solid color-mix(in srgb, var(--orange) 35%, var(--border)); border-radius: 5px; padding: 2px 7px; }
  .usage-reset { font-size: 11px; color: var(--red); }
  .ww { display: flex; align-items: center; gap: 5px; font-size: 10.5px; color: var(--text-muted); cursor: pointer; }
  .ww.disabled { opacity: 0.5; cursor: not-allowed; }
  .refresh { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; color: var(--text-dim); background: none; border: none; cursor: pointer; padding: 4px 7px; border-radius: 6px; }
  .refresh:hover { color: var(--accent-ink); background: var(--bg-active); }
  .refresh .ri { font-size: 12px; display: inline-block; }

  /* ── upcoming list ── */
  .ulist { max-height: 260px; overflow-y: auto; }
  .ucall { display: flex; align-items: center; gap: 10px; padding: 7px 0; border-top: 1px solid var(--border); font-size: 11.5px; }
  .ucall:first-child { border-top: none; }
  .uc-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; padding-left: 9px; border-left: 2px solid transparent; }
  .ucall.acc-client .uc-main, .ucall.acc-inv .uc-main { border-left-color: var(--accent); }
  .ucall.acc-inv .uc-main { border-left-color: var(--green); }
  .ucall.acc-ext .uc-main { border-left-color: var(--orange); }
  .ucall.acc-int .uc-main { border-left-color: var(--border); }
  .uc-t { font-size: 12px; font-weight: 600; color: var(--text); display: flex; align-items: center; gap: 8px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .ucall.acc-int .uc-t { color: var(--text-dim); font-weight: 500; }
  .uc-when { font-family: var(--font-mono); font-size: 10px; color: var(--text-dim); }
  .uc-act { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
  .utag { font-family: var(--font-mono); font-size: 8.5px; font-weight: 600; border-radius: 4px; padding: 1px 6px; border: 1px solid; white-space: nowrap; }
  .utag.client { color: var(--accent-ink); border-color: color-mix(in srgb, var(--accent) 40%, var(--border)); }
  .utag.ext { color: var(--orange); border-color: color-mix(in srgb, var(--orange) 40%, var(--border)); }
  .utag.int { color: var(--text-dim); border-color: var(--border); }

  /* ── chips ── */
  /* These were `.chip` — chip-SHAPED, but every one of them performs an action
     (Download brief · Transcript · Edit · Fullscreen · Delete · Test mic), which is the
     `.today-mini-btn` job, not the toggle-chip job. The app has ONE action-button
     vocabulary; `.chip` (app.css) means selection only. Don't reintroduce per-size
     variants here — they were a pixel of padding apart and read as drift.
     ⚠️ The two STATE modifiers below are unreviewed: `.invited` is green and `.danger` is
     red, both questionable against DESIGN-SYSTEM §1 (green = on-track deal status; red =
     at-risk/overdue) and against the settled call that destructive actions are neutral.
     → `archive/todos/calls-design.md`. */
  .today-mini-btn:disabled { cursor: default; }
  .today-mini-btn.invited { background: color-mix(in srgb, var(--green) 16%, transparent); border-color: color-mix(in srgb, var(--green) 40%, var(--border)); color: var(--green); }
  .today-mini-btn.danger { color: var(--red); border-color: color-mix(in srgb, var(--red) 45%, var(--border)); }
  .today-mini-btn .gs { color: var(--accent-ink); }
  .oldname { color: var(--text-dim); font-weight: 400; font-size: 0.85em; }
  .inline-link { background: none; border: none; padding: 0; color: var(--accent-ink); font: inherit; cursor: pointer; }
  .inline-link:hover { text-decoration: underline; }

  /* ── history records ── */
  .hscroll { max-height: 360px; overflow-y: auto; }
  .sel { min-width: 150px; background: var(--bg-surface); border: 1px solid var(--border); color: var(--text); border-radius: 7px; padding: 6px 9px; font-size: 11.5px; font-family: inherit; }
  .hfilter { min-width: 130px; font-size: 11px; padding: 4px 9px; }
  .hrec { border: 1px solid var(--border); border-radius: 8px; background: var(--bg-surface); margin-top: 9px; overflow: hidden; }
  .hrec:first-child { margin-top: 0; }
  .hrec-h { display: flex; align-items: center; gap: 9px; padding: 9px 11px; cursor: pointer; }
  .hrec-h:hover { background: var(--bg-hover); }
  .rtag { font-family: var(--font-mono); font-size: 8.5px; font-weight: 600; border-radius: 4px; padding: 2px 6px; border: 1px solid; white-space: nowrap; }
  .rtag.meeting { color: var(--accent-ink); border-color: color-mix(in srgb, var(--accent) 40%, var(--border)); }
  .rtag.call { color: var(--blue); border-color: color-mix(in srgb, var(--blue) 40%, var(--border)); }
  .hrec-cli { font-size: 12.5px; font-weight: 600; }
  .hrec-date { font-family: var(--font-mono); font-size: 10px; color: var(--text-dim); }
  .hrec-act { margin-left: auto; display: flex; gap: 6px; }
  .hrec-body { border-top: 1px solid var(--border); padding: 12px 13px; }
  .hrec-body.fullscreen { position: fixed; inset: 40px; z-index: 100; background: var(--bg-surface); border: 1px solid var(--border); border-radius: 10px; overflow-y: auto; box-shadow: 0 12px 48px rgba(0, 0, 0, 0.5); }
  .hrec-tabs { display: flex; align-items: center; gap: 6px; margin-bottom: 10px; }
  .htab { background: none; border: none; border-bottom: 2px solid transparent; color: var(--text-muted); font: inherit; font-size: 12px; padding: 4px 8px; cursor: pointer; }
  .htab.active { color: var(--text); border-bottom-color: var(--accent); font-weight: 600; }
  .hrec-md { font-size: 12.5px; line-height: 1.55; color: var(--text); }
  .hrec-edit { width: 100%; font-family: var(--font-mono); font-size: 12px; padding: 8px; border: 1px solid var(--border); border-radius: 6px; resize: vertical; background: var(--bg-surface); color: var(--text); }

  /* ── set up ── */
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; }
  .row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .modeseg { display: inline-flex; border: 1px solid var(--border); border-radius: 7px; overflow: hidden; margin: 2px 0 6px; }
  .modeseg button { font-family: inherit; font-size: 11px; color: var(--text-muted); background: var(--bg-surface); border: none; border-right: 1px solid var(--border); padding: 6px 11px; cursor: pointer; }
  .modeseg button:last-child { border-right: none; }
  .modeseg button.on { color: var(--text); background: color-mix(in srgb, var(--accent) 12%, transparent); box-shadow: inset 0 -2px 0 var(--accent); }
  .meaning { font-size: 10.5px; color: var(--text-dim); margin-top: 6px; }
  .meaning b { color: var(--text-muted); font-weight: 600; }
  .meet-note { font-size: 11.5px; color: var(--text-muted); margin-top: 8px; }
  .meet-note a { color: var(--accent-ink); }

  /* ── call settings subpage ── */
  /* Back crumb uses the shared `.back-crumb` in app.css. This page keeps only the
     spacing below it, which is its own. */
  .back-crumb { margin-bottom: 8px; }
  .set-title { font-family: inherit; font-size: 24px; font-weight: 600; margin-bottom: 4px; }
  .set-sub { font-size: 11.5px; color: var(--text-dim); margin-bottom: 14px; }
  .sblock { border: 1px solid var(--border); border-radius: 9px; background: var(--bg-surface); margin-bottom: 10px; overflow: hidden; }
  .sblock .sh { padding: 9px 13px; background: var(--bg-hover); border-bottom: 1px solid var(--border); font-family: inherit; font-size: 14px; font-weight: 600; }
  .sblock .sbb { padding: 11px 13px; }
  .sblock .d { font-size: 10.5px; color: var(--text-dim); margin-bottom: 9px; }
  .dict { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-top: 1px solid var(--border); font-size: 11.5px; }
  .dict:first-of-type { border-top: none; }
  .dict .k { font-family: var(--font-mono); color: var(--text); min-width: 64px; }
  .dict .arr { color: var(--text-dim); }
  .dict .v { color: var(--text-muted); flex: 1; }
  .dict .x { color: var(--text-dim); cursor: pointer; font-size: 13px; background: none; border: none; padding: 2px 4px; }
  .dict .x:hover { color: var(--text); }
  .dict .x.pron-test { font-size: 11px; }
  .dictadd { display: flex; gap: 7px; flex-wrap: wrap; align-items: center; margin-top: 9px; padding-top: 9px; border-top: 1px solid var(--border); }
  .inp { flex: 1; min-width: 100px; background: var(--bg-surface); border: 1px solid var(--border); color: var(--text); border-radius: 6px; padding: 6px 9px; font-size: 11.5px; font-family: inherit; }
  .sld { display: flex; align-items: center; gap: 10px; }
  .sld input { flex: 1; accent-color: var(--accent); }
  .sld .v { font-family: var(--font-mono); font-size: 11px; min-width: 50px; text-align: right; color: var(--text-muted); }
  .vrow { display: flex; align-items: stretch; border: 1px solid var(--border); border-radius: 8px; overflow: hidden; background: var(--bg-surface); margin-bottom: 7px; }
  .vrow.sel { border-color: var(--accent); background: color-mix(in srgb, var(--accent) 10%, transparent); }
  .vrow .vm { flex: 1; text-align: left; background: none; border: none; color: var(--text); padding: 8px 11px; cursor: pointer; }
  .vrow .vm b { font-size: 12px; display: block; }
  .vrow .vm span { font-size: 10px; color: var(--text-dim); }
  .vrow .vp { width: 38px; border: none; border-left: 1px solid var(--border); background: none; color: var(--text-muted); cursor: pointer; }
  .vrow .vp:hover { color: var(--accent-ink); background: var(--bg-hover); }

  @media (max-width: 640px) {
    .grid2 { grid-template-columns: 1fr; }
    .toolbar { flex-wrap: wrap; }
  }
</style>
